import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import React from 'react';
import clsx from 'clsx';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import ImageSelector from '@di-shared/features/common/ImageSelector';
import LottieSelector from '@di-shared/features/common/LottieSelector';
import useUnitConverter from '@di-shared/utils/useUnitConverter';
import useDashedDatetimeFormatter from '@di-shared/utils/useDashedDatetimeFormatter';

function SafeDrivingContentsLoadingKr() {
  const {t} = useInferableTranslation();
  const {convertDistanceValue, convertDistanceSymbol} = useUnitConverter();
  const DashedDatetimeFormatter = useDashedDatetimeFormatter();

  return (
    <div className="wrap">
      <Header left={<HeaderBackButton />}>
        <h1 className="headline_xxsmall nav_center_title">{t('T40004')}</h1>
      </Header>
      <main className="content page_safe_driving">
        <section className="section section_safe_driving">
          <h2 className="blind">{t('T20125')}</h2>
          <div className="my_driving_score skeleton">
            <div className="my_score_box">
              <div className="my_score_imgbox skeleton">
                <LottieSelector
                  id={'safe_driving_score_good_light'}
                  kiaLightSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good_light.json')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good.json')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good_light.json')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good.json')}
                  genesisLightSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good_light.json')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/json/safe_driving_score_good.json')}
                  loop={true}
                  autoplay={true}
                />
              </div>
              <div className="my_score_info">
                <div className="my_score_point">
                  <span className="my_score_value">{54}</span>
                  <span className="my_score_unit">{t('T10056')}</span>
                </div>
                <span className="my_score_update">{t('T10077', {date: DashedDatetimeFormatter.format(new Date(), 'datetime')})}</span>
              </div>
            </div>
            <div className="score_factor">
              <ul className="score_factor_list">
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('T10011')}</span>
                  <span className="score_factor_value">{t('T10014_$count', {count: 3})}</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('T10012')}</span>
                  <span className="score_factor_value">{t('T10014_$count', {count: 4})}</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('T10013')}</span>
                  <span className="score_factor_value">{t('T10014_$count', {count: 5})}</span>
                </li>
                <li className="score_factor_item">
                  <span className="score_factor_title">{t('T20000')}</span>
                  <span className="score_factor_value">{t('T10014_$count', {count: 6})}</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="section_container safe_driving_standard skeleton">
            <div className="driving_accordion type_container">
              <div className="driving_accordion_item">
                <h3>
                  <button
                    type="button"
                    className="driving_accordion_btn"
                    aria-expanded={true}
                    aria-controls="accordion_content01"
                  >
                    <span className="driving_accordion_title">{t('T20126')}</span>
                    <span
                      className="ic ic_arrow_down_16px"
                      aria-hidden="true"
                    ></span>
                  </button>
                </h3>
                <div
                  className="driving_accordion_content"
                  id="accordion_content01"
                  style={{display: 'block'}}
                >
                  <ul className="score_standard_list">
                    <li className="score_standard_item">
                      <div className="score_standard_icon">
                        <span className="ic ic_calendar_24px"></span>
                      </div>
                      <div className="score_standard_info">
                        <strong className="score_standard_title">{t('T20127')}</strong>
                        <span className="score_standard_desc">
                          {t('T20128_$fromDate_$toDate_$totalDays', {
                            fromDate: '2024-05-10',
                            toDate: DashedDatetimeFormatter.format(new Date(), 'date'),
                            totalDays: '2024-05-11',
                          })}
                        </span>
                      </div>
                    </li>
                    <li className="score_standard_item">
                      <div className="score_standard_icon">
                        <span
                          className="ic ic_drive_distance_24px"
                          aria-hidden="true"
                        ></span>
                      </div>
                      <div className="score_standard_info">
                        <strong className="score_standard_title">{t('T20129')}</strong>
                        <span
                          className="score_standard_desc"
                          style={{whiteSpace: 'pre-wrap'}}
                        >
                          {t('T20130_$days_$distance', {
                            days: 4,
                            distance: `${convertDistanceValue(1000)}${convertDistanceSymbol(1000)}`,
                          })}
                        </span>
                      </div>
                    </li>
                  </ul>

                  <div className="driving_banner type_square">
                    <a
                      role="button"
                      className="driving_banner_box"
                    >
                      <span className="driving_banner_txtbox type_reverse">
                        <strong className="driving_banner_title body_medium_b">{t('T20131')}</strong>
                        <span className="driving_banner_desc label_small_b">{t('T20132')}</span>
                      </span>
                      <span className="driving_banner_imgbox">
                        <ImageSelector
                          className="driving_banner_img"
                          alt="driving_banner_img"
                          kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          hyundaiLightSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          genesisLightSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                          genesisDarkSrc={() => import('@di-shared/assets/img/banner/banner_score_guide.png')}
                        />
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="section_container safe_driving_daily skeleton">
            <div className="section_container_header">
              <h3 className="title_xxsmall">{t('T20009')}</h3>
            </div>
            {/*<SafeDrivingDailyScoreGraph*/}
            {/*  // 항상 90일치의 그래프가 보이도록*/}
            {/*  data={[...new Array(MAX_GRAPH_LENGTH - 0).fill(null).ubi]}*/}
            {/*  fromDate={subDate(new Date(), {days: MAX_GRAPH_LENGTH - 1})}*/}
            {/*/>*/}

            <ul className="statistics_info_list">
              <li className="statistics_info_item">
                <span className="statistics_info_key">{t('T20010')}</span>
                <span className="statistics_info_val">
                  <span className="statistics_info_number">{50}</span>
                  <span className="statistics_info_unit">{t('T10056')}</span>
                </span>
              </li>
              <li className="statistics_info_item">
                <span className="statistics_info_key">{t('T20011')}</span>
                <span className="statistics_info_val">
                  <span className="statistics_info_number">{60}</span>
                  <span className="statistics_info_unit">{t('T10056')}</span>
                </span>
              </li>
            </ul>
          </div>
          <div className="section_container safe_driving_major skeleton">
            <div className="section_container_header">
              <h3 className="title_xxsmall">{t('T20013')}</h3>
            </div>
            <p className="section_container_desc">{t('T20014')}</p>

            <div className="tabbar type_brick">
              <button
                type="button"
                className={clsx('tabbar_item body_small_b')}
              >
                {t('T20015')}
              </button>
              <button
                type="button"
                className={clsx('tabbar_item body_small_b')}
              >
                {t('T20016')}
              </button>
            </div>

            <div className="tab_content driving_safety">
              <ul className="major_driving_list">
                <li className="major_driving_item">
                  <strong className="major_driving_rank">
                    <span className="rank_text">{t('T20017_$n', {n: 1})}</span>
                    <span className="count_text">
                      <span className="count_value">{20}</span>
                      <span className="count_unit">{t('T20133')}</span>
                    </span>
                  </strong>
                  <div className="major_driving_info">
                    <div className="major_driving_action">
                      <span className="driving_action">
                        <span className="action_text">{t('T10011')}</span>
                        <span className="action_value">{3}</span>
                      </span>
                      <span className="divider divider_vertical"></span>
                      <span className="driving_action">
                        <span className="action_text">{t('T10012')}</span>
                        <span className="action_value">{4}</span>
                      </span>
                      <span className="divider divider_vertical"></span>
                      <span className="driving_action">
                        <span className="action_text">{t('T10013')}</span>
                        <span className="action_value">{5}</span>
                      </span>
                      <span className="ic ic_arrow_forward_12px"></span>
                    </div>
                    <div className="major_driving_detail">
                      <span className="driving_date">{'20240243'}</span>
                      <span className="divider divider_vertical"></span>
                      <span className="driving_place">{'ABCD'}</span>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div className="section_container safe_driving_night skeleton">
            <div className="section_container_header">
              <h3 className="title_xxsmall">{t('T20000')}</h3>
            </div>
            <p
              className="section_container_desc"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T20136')}
            </p>

            <ul className="statistics_info_list">
              <li className="statistics_info_item">
                <span
                  className="statistics_info_key"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('T20137')}
                </span>
                <span className="statistics_info_val">
                  <span className="statistics_info_number">{36}</span>
                  <span className="statistics_info_unit">{t('T20133')}</span>
                </span>
              </li>
              <li className="statistics_info_item">
                <span
                  className="statistics_info_key"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('T20138')}
                </span>
                <span className="statistics_info_val">
                  <span className="statistics_info_number">{5}</span>
                  <span className="statistics_info_unit">{t('T20133')}</span>
                </span>
              </li>
            </ul>
          </div>
          <div className="section_container safe_driving_ubi skeleton">
            <div className="section_container_header">
              <h3 className="title_xxsmall">{t('T20139')}</h3>
            </div>
            <p className="section_container_desc">
              {t('T20140_$score_$discount', {
                score: 44,
                discount: 12,
              })}
            </p>
            <div className="container_banner_imgbox">
              <ImageSelector
                alt="insurance_benefit_iocn"
                kiaLightSrc={() => import('@di-shared/assets/img/safe_driving_score/insure_benefit.png')}
                kiaDarkSrc={() => import('@di-shared/assets/img/safe_driving_score/insure_benefit.png')}
                hyundaiLightSrc={() => import('@di-shared/assets/img/safe_driving_score/insure_benefit.png')}
                hyundaiDarkSrc={() => import('@di-shared/assets/img/safe_driving_score/insure_benefit.png')}
                genesisLightSrc={() => import('@di-shared/assets/img/safe_driving_score/insure_benefit.png')}
                genesisDarkSrc={() => import('@di-shared/assets/img/safe_driving_score/insure_benefit.png')}
              />
            </div>
            <a
              role="button"
              className="section_container_full_btn"
            >
              <span className="blind">{t('T20141')}</span>
            </a>
          </div>
          <div className="section_container safe_driving_predict skeleton">
            <div className="section_container_header">
              <h3 className="title_xxsmall">{t('T20142')}</h3>
            </div>
            <p
              className="section_container_desc"
              id="scorePredictDescription"
              role="tabpanel"
              aria-labelledby="scorePredict500"
              aria-live="polite"
              style={{whiteSpace: 'pre-wrap'}}
            >
              {t('T20143_$distance_$score', {
                distance: `300`,
                score: 55,
              })}
            </p>
            <div className="score_predict">
              <span className="speech_bubble">
                <span className="blind">{t('T20144')}</span>
                <span className="score_predict_point">{t('T20006_$score', {score: 55})}</span>
                <span className="blind">{t('T20145')}</span>
                <span className="score_predict_distance">
                  {convertDistanceValue(2900)}
                  {convertDistanceSymbol(2900)}
                </span>
              </span>
              <span className="predict_graph_bar">
                <span className={clsx('bar_current')}>
                  <span
                    className="speech_bubble"
                    aria-hidden="true"
                  >
                    <span className="score_predict_point">{t('T20006_$score', {score: 55})}</span>
                    <span className="score_predict_distance">
                      {convertDistanceValue(1000)}
                      {convertDistanceSymbol(1000)}
                    </span>
                  </span>
                </span>
              </span>
              <div
                className="score_predict_control"
                role="tablist"
              >
                <button
                  type="button"
                  className="score_predict_btn"
                  id="scorePredictCurrent"
                  role="tab"
                  aria-selected="false"
                  aria-controls="scorePredictDescription"
                  disabled={true}
                >
                  {t('T20146')}
                </button>
                <button
                  type="button"
                  className="score_predict_btn"
                  id="scorePredict100"
                  role="tab"
                  aria-selected={true}
                  aria-controls="scorePredictDescription"
                >
                  +{convertDistanceValue(100)}
                  {convertDistanceSymbol(100)}
                </button>
                <button
                  type="button"
                  className="score_predict_btn"
                  id="scorePredict300"
                  role="tab"
                  aria-selected={true}
                  aria-controls="scorePredictDescription"
                >
                  +{convertDistanceValue(300)}
                  {convertDistanceSymbol(300)}
                </button>
                <button
                  type="button"
                  className="score_predict_btn"
                  id="scorePredict500"
                  role="tab"
                  aria-selected={true}
                  aria-controls="scorePredictDescription"
                >
                  +{convertDistanceValue(500)}
                  {convertDistanceSymbol(500)}
                </button>
                <button
                  type="button"
                  className="score_predict_btn"
                  id="scorePredict1000"
                  role="tab"
                  aria-selected={true}
                  aria-controls="scorePredictDescription"
                >
                  +{convertDistanceValue(1000)}
                  {convertDistanceSymbol(1000)}
                </button>
              </div>
            </div>
          </div>
          <div className="driving_banner type_squar skeleton">
            <button
              type="button"
              className="driving_banner_box"
            >
              <span className="driving_banner_txtbox">
                <strong className="driving_banner_title body_large_b">{t('T20147')}</strong>
                <span
                  className="driving_banner_desc body_small_r"
                  style={{whiteSpace: 'pre-wrap'}}
                >
                  {t('T20148')}
                </span>
              </span>
              <span className="driving_banner_imgbox">
                <ImageSelector
                  className="driving_banner_img"
                  alt="driving_banner_img"
                  kiaLightSrc={() => import('@di-shared/assets/img/banner/banner_improve_service_light.png')}
                  kiaDarkSrc={() => import('@di-shared/assets/img/banner/banner_improve_service.png')}
                  hyundaiLightSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_improve_service_light.png')}
                  hyundaiDarkSrc={() => import('@di-shared/assets/img/banner/hyundai/banner_improve_service.png')}
                  genesisLightSrc={() => import('@di-shared/assets/img/banner/genesis/banner_improve_service_light.png')}
                  genesisDarkSrc={() => import('@di-shared/assets/img/banner/genesis/banner_improve_service.png')}
                />
              </span>
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}

export default SafeDrivingContentsLoadingKr;
